import React, { useState, useEffect, useRef } from "react";
import "./Juliette.css";

const START_DATE = new Date("2024-10-25");

function getDaysTogether() {
  const now = new Date();
  const diff = now - START_DATE;
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function FloatingHearts() {
  const hearts = Array.from({ length: 18 }, (_, i) => i);
  return (
    <div className="hearts-container" aria-hidden="true">
      {hearts.map((i) => (
        <span
          key={i}
          className="floating-heart"
          style={{
            left: `${(i * 5.8 + 2) % 100}%`,
            animationDelay: `${(i * 0.7) % 8}s`,
            animationDuration: `${6 + (i % 5)}s`,
            fontSize: `${14 + (i % 4) * 6}px`,
            opacity: 0.15 + (i % 5) * 0.1,
          }}
        >
          {i % 3 === 0 ? "❤️" : i % 3 === 1 ? "🩷" : "💕"}
        </span>
      ))}
    </div>
  );
}

function CountUp({ target, suffix = "" }) {
  const [count, setCount] = useState(0);
  const raf = useRef(null);

  useEffect(() => {
    const duration = 1200;
    const start = performance.now();

    const step = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) raf.current = requestAnimationFrame(step);
    };

    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target]);

  return (
    <span className="count-up">
      {count.toLocaleString("fr-FR")}
      {suffix}
    </span>
  );
}

function StatCard({ emoji, label, value, suffix, color }) {
  return (
    <div className="stat-card" style={{ "--card-color": color }}>
      <div className="stat-emoji">{emoji}</div>
      <div className="stat-value">
        {typeof value === "string" ? (
          <span className="count-up">{value}</span>
        ) : (
          <CountUp target={value} suffix={suffix} />
        )}
      </div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function Notebook({ onClick }) {
  return (
    <button className="notebook-btn" onClick={onClick} aria-label="Ouvrir la note">
      <div className="notebook">
        <div className="notebook-rings">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="ring" />
          ))}
        </div>
        <div className="notebook-cover">
          <div className="notebook-lines">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="notebook-line" />
            ))}
          </div>
          <div className="notebook-heart">♥</div>
        </div>
      </div>
      <p className="notebook-hint">Clique pour lire ta note</p>
    </button>
  );
}

function NotePopup({ onClose }) {
  const lines = [
    "Ma Juliette,",
    "",
    "Je l'aime. Elle prend soin de moi, c'est la personne",
    "la plus élégante que je connaisse. Elle est très belle",
    "(genre déesse grecque). On s'entend trop bien. On a des",
    "souvenirs trop chouettes à deux, que ce soit au Sénégal,",
    "en Grèce, à Cannes, à Disney, etc.. Elle fait des câlins",
    "trop doux. Elle a un sourire trop mignon. Elle est",
    "toujours grave attentionnée. Elle me calme. Elle est",
    "super généreuse (parfois trop). Elle me fait évoluer.",
    "Elle connaît mes défauts parfois mieux que moi et",
    "m'aide à les améliorer. Je peux parler de tout avec elle.",
    "Elle me soutient toujours, même dans les mauvais moments.",
    "Je pense toujours à elle, je devrais lui dire plus souvent.",
    "Elle me juge pas. Avec elle je me sens en sécurité, elle",
    "sait toujours me rassurer. Notre histoire est super belle,",
    "on s'est rencontré dans des huttes, on a voyagé ensemble,",
    "on a rigolé ensemble, on a exploré ensemble, dans l'amour.",
    "Elle dégage un truc incroyable quand elle est heureuse.",
    "On a à peu près les même goûts de films. Je me sens trop",
    "bien quand je passe des moments avec elle, quand on fait",
    "des cookies, que je lui fais des crêpes, quand on va au",
    "ciné ou qu'on regarde une série. Je…",
    "",
    "— Ton Quentin 🩷",
  ];

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div
        className="popup-note"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Note pour Juliette"
      >
        <button className="popup-close" onClick={onClose} aria-label="Fermer">
          ×
        </button>
        <div className="popup-paper">
          <div className="popup-lines-bg">
            {Array.from({ length: 14 }).map((_, i) => (
              <div key={i} className="popup-bg-line" />
            ))}
          </div>
          <div className="popup-text">
            {lines.map((line, i) =>
              line === "" ? (
                <br key={i} />
              ) : (
                <p
                  key={i}
                  className="popup-line"
                  style={{ animationDelay: `${i * 0.06}s` }}
                >
                  {line}
                </p>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Juliette() {
  const [showNote, setShowNote] = useState(false);
  const days = getDaysTogether();

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  return (
    <div className="juliette-page">
      <FloatingHearts />

      <header className="juliette-header">
        <div className="title-badge">Pour toi ✨</div>
        <h1 className="juliette-title">
          Juliette <span className="title-heart">♥</span>
        </h1>
        <p className="juliette-subtitle">
          Mon petit dashboard d'amour, rien que pour toi
        </p>
      </header>

      <section className="stats-grid">
        <StatCard
          emoji="📅"
          label="jours ensemble"
          value={days}
          color="#FF6B8A"
        />
        <StatCard
          emoji="🌙"
          label="nuits à tes côtés"
          value={200}
          suffix="+"
          color="#C084FC"
        />
        <StatCard
          emoji="🎬"
          label="cinés ensemble"
          value={10}
          suffix="+"
          color="#F59E0B"
        />
        <StatCard
          emoji="📸"
          label="souvenirs créés"
          value="∞"
          color="#34D399"
        />
      </section>

      <section className="notebook-section">
        <Notebook onClick={() => setShowNote(true)} />
      </section>

      {showNote && <NotePopup onClose={() => setShowNote(false)} />}
    </div>
  );
}
